import React, {
	PropsWithChildren,
	memo,
	useMemo,
	useCallback,
	useLayoutEffect,
	useEffect,
} from "react";
import { cloneDeep } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import { StyledInput } from "../styled";
import {
	HandleEventsProps,
	useHandleEvents,
	ValueAccessors,
} from "../../hooks";
import useKeyBind from "../../../../../../hooks/useKeyBind";
import PointOrderModalTableBase from "../..";

const EntranceBase: React.FC<EntranceBase.Props> = ({
	rowData,
	rowIndex,
	dataKey,
	colIndex,
	setData,
	height,
	handleEventsOptions,
	countRow,
	reloadCell,
	setReloadCell,
}) => {
	const idCel = useMemo(() => rowData.raw.id, [rowData.raw.id]);
	const isLastRow = useMemo(() => idCel === countRow, [countRow, idCel]);

	const {
		inputValue,
		save,
		setSave,
		onChangeInputValue,
		onEnterToInput,
		onExistFromInput,
		onClickToInput,
		setColIndex,
		setDataKey,
		setInputId,
		setRowId,
		setRowIndex,
		setInputValue,
		nextElementById,
		reset,
	} = useHandleEvents(
		{
			idCel,
			isLastRow,
		},
		handleEventsOptions,
	);

	useLayoutEffect(() => {
		setColIndex(`${colIndex}`);
		setDataKey(dataKey);
		setInputId(`${idCel + dataKey}`);
		setRowId(idCel);
		setRowIndex(`${rowIndex}`);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const debouncedSave = useDebouncedCallback((value: string) => {
		rowData.saveType = "entrance";
		rowData.raw.saveType = "entrance";
		setInputValue(value);
		setSave(true);
	}, 300);

	const updatedData = useCallback(
		(value: string) => {
			if (!value) return;
			const house = rowData.raw.house || rowData.raw.number || null;

			if (!house) {
				if (`${rowData.raw.entrance}`.trim() === `${value}`.trim()) {
					setSave(false);
				} else {
					debouncedSave(value);
					setInputValue(value);
					return;
				}
			}

			if (`${rowData.raw.entrance}`.trim() === `${value}`.trim()) {
				setSave(false);
			} else if (rowData.raw.saveType === "street") {
				debouncedSave(value);
				setInputValue(value);
				rowData.saveType = "entrance";
				rowData.raw.saveType = "entrance";
				return;
			}

			const newData = cloneDeep(rowData);
			newData.entrance = value || "";
			newData.raw.entrance = value || "";
			newData.raw.type = "address";
			newData.save = true;
			newData.raw.save = true;
			newData.saveType = "entrance";
			newData.raw.saveType = "entrance";
			setData(newData);
			if (`${rowData.raw.entrance}`.trim() === `${value}`.trim()) {
				setInputValue(null);
			}

			setSave(false);
		},
		[debouncedSave, rowData, setData, setInputValue, setSave],
	);

	const updatedDataOnBlurCapture = useCallback(
		(value: string) => {
			const newData = cloneDeep(rowData);
			newData.entrance = value || "";
			newData.raw.entrance = value || "";
			newData.raw.type = "address";
			newData.save = true;
			newData.raw.save = true;
			newData.saveType = "entrance";
			newData.raw.saveType = "entrance";
			setData(newData);
			if (`${rowData.raw.entrance}`.trim() === `${value}`.trim()) {
				setInputValue(null);
			}

			setSave(false);
		},
		[rowData, setData, setInputValue, setSave],
	);

	const isDisabled = useMemo(() => {
		const list: boolean[] = [
			!rowData?.raw?.house, // eslint-disable-next-line regex/invalid
			rowData?.raw?.street === "центр",
			// eslint-disable-next-line regex/invalid
			rowData?.raw?.street === "центр",
			rowData?.raw?.street === "center",
		];
		return list.includes(true);
	}, [rowData?.raw?.house, rowData?.raw?.street]);

	useEffect(() => {
		if (
			reloadCell === `${idCel}street` ||
			reloadCell === `${idCel}settlement` ||
			reloadCell === `${idCel}house` ||
			isDisabled
		) {
			setTimeout(() => {
				setInputValue(null);
				reset();
				setSave(false);
			}, 0);

			setReloadCell("");
		}
	}, [
		reloadCell,
		idCel,
		setSave,
		setInputValue,
		reset,
		setReloadCell,
		isDisabled,
	]);

	useKeyBind(["Enter"], (event) => {
		event.stopPropagation();
		event.preventDefault();
	});

	useKeyBind(["Tab"], (event) => {
		event.stopPropagation();
	});

	useEffect(() => {
		if (inputValue !== null && save) {
			updatedData(inputValue);
		}
	}, [inputValue, save, updatedData]);

	useEffect(() => {
		const { entrance, street, house, city } = rowData;

		const isAllAddressFieldsFilled = [entrance, street, house, city].every(
			(val) => typeof val === "string" && val.trim() !== "",
		);

		if (isAllAddressFieldsFilled && isLastRow) {
			// nextFocus();
			nextElementById("order-save-id");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLastRow, rowData.entrance]);

	const propsInput = useMemo(() => {
		const baseValue = `${rowData.raw?.entrance || ""}`;
		return {
			value: inputValue !== null ? inputValue : baseValue,
		};
	}, [rowData.raw?.entrance, inputValue]);

	const onFocus = useCallback((e) => e.target.select(), []);

	const size = useMemo(
		() =>
			propsInput.value.length > 2 ? propsInput.value.length * 1.25 : 2,
		[propsInput.value.length],
	);

	return (
		<StyledInput
			size={size}
			height={height}
			id={`${idCel + dataKey}`}
			aria-label={dataKey}
			aria-rowindex={rowIndex}
			aria-colindex={colIndex}
			autoComplete="one-time-code"
			onChange={(event) => {
				if (isDisabled) return;
				onChangeInputValue(event);
			}}
			onKeyUp={onEnterToInput}
			onKeyDown={onExistFromInput}
			onClick={onClickToInput}
			onFocus={onFocus}
			onBlurCapture={() => {
				if (rowData?.raw?.entrance !== propsInput.value && !save) {
					updatedDataOnBlurCapture(propsInput.value);
				}
			}}
			onCut={(event) => {
				event.preventDefault();
			}}
			onDragOver={(event) => {
				event.preventDefault();
			}}
			onDrop={(event) => {
				event.preventDefault();
			}}
			{...propsInput}
		/>
	);
};

declare namespace EntranceBase {
	interface Props extends PropsWithChildren {
		rowData: PointOrderModalTableBase.OrderModal;
		setData: (data: PointOrderModalTableBase.OrderModal) => void;
		rowIndex?: number;
		dataKey: ValueAccessors | string;
		colIndex: number;
		height?: number;
		handleEventsOptions?: HandleEventsProps;
		countRow?: number;
		reloadCell: string;
		setReloadCell: React.Dispatch<React.SetStateAction<string>>;
		loadingChangePoint: boolean;
		setLoadingChangePoint: React.Dispatch<React.SetStateAction<boolean>>;
	}
}

export const Entrance = memo(EntranceBase);

export default EntranceBase;
